import moment from 'moment';
import { snakeCase, mapKeys, get, getOr, compact } from 'lodash/fp';
// @ts-ignore ts-migrate(7016) FIXME: Could not find a declaration file for module '@whi... Remove this comment to see the full error message
import commonMessages from '@whitelabel/helpers/messages/commonMsg';
import { IDictionary, IDOB } from '@whitelabel/helpers/types';
import { dateISOtoDDMMYYYY } from '../date';
import { API_HOST } from '../api';
import { getQuote } from '../bookings';
import { SAVED_CLAIMS, MIME_TYPES, stateMapUSCA } from '../constants';
import { getClaimURLQueryParams } from '../fnol';
import { IBooking, IPolicyholder, IUser, ICustomer, IFNOLSection, IFNOLValues } from '../types';

// Returns a new customer object with birthDate formatted for a masked input field
export const customerAPIToForm = ({ birthDate, ...attributes }: any) => ({
  ...attributes,
  birthDate: birthDate ? dateISOtoDDMMYYYY(birthDate) : '',
});

export const formatBirthDateToForm = (birthDate?: string): IDOB | '' => {
  if (!birthDate) {
    return '';
  }

  const [dobDay, dobMonth, dobYear] = dateISOtoDDMMYYYY(birthDate).split('/');
  return { dobDay, dobMonth, dobYear };
};

export const formatBirthDateToAPI = (birthDate?: IDOB): string | null => {
  const { dobYear, dobMonth, dobDay } = birthDate || {};
  if (!dobYear || !dobMonth || !dobYear) {
    return null;
  }
  return `${dobYear}-${dobMonth}-${dobDay}`;
};

//  Convert a string to snake case with few custom convertion logic
const customSnakeKeys = (key: any) => {
  const matches = {
    birthDate: 'date_of_birth',
    postcode: 'postal_code',
    address1: 'address1',
    address2: 'address2',
    region: 'state_province',
  };
  // @ts-ignore ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  return matches[key] || snakeCase(key);
};

//  Returns an object with field name keys similar to response. Eg: first name transform to first_name (not firstName)
export const updateClaimantKeys = (claimant: any) => mapKeys(customSnakeKeys)(claimant);

export const updateStateCode = (claimant: IPolicyholder | ICustomer) => {
  if (
    ['US', 'CA'].includes(claimant.country as string) &&
    Object.keys(stateMapUSCA).includes(claimant.region?.toLowerCase() as string)
  ) {
    return {
      ...claimant,
      region: stateMapUSCA[claimant.region?.toLowerCase() as keyof typeof stateMapUSCA],
    };
  }
  return claimant;
};

/**
 * Returns reformatted sectionFields object for use in initialValues
 * @param {Array} section - section from getFNOL response
 * @param {Object} policyholder - policyholder data from booking
 * @param {Object} customer - user's profile data
 * @returns {Object} e.g. { sectionName: { fieldName: '' } }
 */

export const fnolSectionAPIToForm = (
  { name: sectionName, sectionFields }: IFNOLSection,
  { policyholder, currency, id }: IBooking,
  customer?: ICustomer,
): IFNOLValues['sectionFields'] => {
  const updateStateCodePolicyholder = updateStateCode(policyholder);
  const policyholderWithUpdatedKeys = updateClaimantKeys(updateStateCodePolicyholder);

  let customerWithUpdatedKeys: IDictionary<unknown>;
  if (customer) {
    const updateStateCodeCustomer = updateStateCode(customer);
    customerWithUpdatedKeys = updateClaimantKeys(updateStateCodeCustomer);
  }

  return {
    [sectionName]: sectionFields.reduce(
      (fields: IFNOLValues['sectionFields']['sectionName'], { name, type, validation, meta }) => {
        const fnolAnswers = JSON.parse(localStorage.getItem(SAVED_CLAIMS) as string)?.[id];

        if (fnolAnswers?.sectionFields[sectionName]) {
          fields[name] = fnolAnswers.sectionFields[sectionName][name] ?? '';
          if (type === 'currencyAmount') {
            return {
              [`${name}_currency`]: fnolAnswers.sectionFields[sectionName][`${name}_currency`] ?? currency,
              ...fields,
            };
          }
        } else {
          const value = sectionName === 'claimant_section' ? policyholderWithUpdatedKeys[name] || '' : '';
          fields[name] = ['set', 'file'].includes(type) ? [] : getOr(value, 'numeric.min', validation);
          if (type === 'currencyAmount') {
            return {
              [`${name}_currency`]: currency,
              ...fields,
            };
          }
          if (
            customerWithUpdatedKeys &&
            Object.keys(customerWithUpdatedKeys).includes(name) &&
            !!customerWithUpdatedKeys[name]
          ) {
            fields[name] = customerWithUpdatedKeys[name] as string;
          }
        }
        if (meta!.display === 'dob') {
          fields[name] =
            typeof fields[name] === 'string' ? formatBirthDateToForm(fields[name] as string) : fields[name] ?? '';
        }

        return fields;
      },
      {},
    ),
  };
};

function convertSectionsToObject(sections: any) {
  return sections.reduce((newSections: any, { name: sectionName, sectionFields }: any) => {
    newSections[sectionName] = sectionFields.reduce((fields: any, { name, type, validation }: any) => {
      fields[name] = {
        type,
        validation,
      };

      return fields;
    }, {});
    return newSections;
  }, {});
}

/**
 * Returns an array of sectionFields ready for the XClaim POST claim request
 * @param {Object} values - values submitted from the FNOL form
 * @returns {Array} [{ name: 'sectionName', fields: [{ name: 'fieldName', value: 'fieldValue' }] }]
 */
export const fnolFormtoAPI = ({ sectionFields, ...fnolValues }: any, sections: any, bookingPolicyHolder: any) => {
  const sectionsObj = convertSectionsToObject(sections);
  const mapFieldsForAPI = (sectionName: any) =>
    Object.keys(sectionFields[sectionName]).map((name) => {
      let value = sectionFields[sectionName][name];
      if (value === undefined || (Array.isArray(value) && value.length === 0)) {
        return undefined;
      }
      if (value !== '' && get(`${sectionName}.${name}.validation.float`, sectionsObj)) {
        value = Number.parseFloat(value).toFixed(2);
      }

      if (name === 'date_of_birth') {
        value = formatBirthDateToAPI(value);
      }
      return {
        name,
        value,
      };
    });
  //  the final object we send is the latest form values plus those properties of booking.policyholder which
  //  are not part of the form. User entered form values always wins
  sectionFields.claimant_section = {
    ...updateClaimantKeys(bookingPolicyHolder),
    ...sectionFields.claimant_section,
  };
  return {
    ...fnolValues,
    sectionFields: Object.keys(sectionFields)
      .map((sectionName) => ({
        name: sectionName,
        fields: compact(mapFieldsForAPI(sectionName)),
      }))
      .filter((sectionField) => Boolean(sectionField.name)),
  };
};

// Returns props object for FormField component based on FNOL field data
export const fnolFieldToProps = (
  { type, name, meta, options, validation }: any,
  booking: any,
  quoteID: any,
  intl: any,
  user: IUser,
) => {
  switch (type) {
    case 'phone':
      return { type: 'tel' };
    case 'string':
      // case 'string' name === 'phone' has been replaced by case 'phone'
      // leave this if block for transition,
      // todo: will this if block in the future
      if (name === 'phone') {
        return { type: 'tel' };
      }
      if (name === 'email') {
        return { type: 'email' };
      }
      return {
        type: meta.display === 'large' ? 'textarea' : 'text',
        minLength: get('length.min', validation),
        maxLength: get('length.max', validation),
      };

    case 'content':
      return {
        type,
        content: meta.content,
      };

    case 'email':
    case 'url':
      return { type: 'text' };

    case 'numeric': {
      return { type: 'number', min: get('numeric.min', validation), max: get('numeric.max', validation) };
    }

    case 'date':
      return meta.display === 'dob'
        ? { type: 'dob', format: 'DD/MM/YYYY', placeholder: '' }
        : { type, format: 'YYYY-MM-DD', placeholder: '' };

    case 'datetime': {
      const props = { type, format: 'YYYY-MM-DD HH:mm:ss', placeholder: '' };

      if (name === 'loss_at') {
        const quote = getQuote(quoteID, booking);
        // @ts-ignore ts-migrate(2339) FIXME: Property 'minDate' does not exist on type '{ type:... Remove this comment to see the full error message
        props.minDate = moment(quote.confirmedAt).startOf('day').toISOString();
        // @ts-ignore ts-migrate(2339) FIXME: Property 'maxDate' does not exist on type '{ type:... Remove this comment to see the full error message
        props.maxDate = moment().endOf('day').toISOString();
      }

      return props;
    }

    case 'boolean':
    case 'enum':
      return { type: 'select', options };

    case 'set':
      return { type: 'checkbox', options, large: 'true' };

    case 'file': {
      const acceptedFileTypes: any = [];
      const allowedFileTypes: any = [];

      const { bookingID, quoteID, securityToken } = getClaimURLQueryParams(window.location.search);
      // only append security token for non-logged in usecase
      const searchParams = user ? '' : `?${new URLSearchParams({ security_token: securityToken || '' })}`;

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      validation?.file?.mimes?.forEach((fileType: any) => {
        // @ts-ignore ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        if (MIME_TYPES[fileType]) {
          // @ts-ignore ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          acceptedFileTypes.push(...MIME_TYPES[fileType]);
          allowedFileTypes.push(fileType.toUpperCase());
        }
      });
      const isAuthRequired = !!user || !securityToken;
      return {
        type,
        maxFileSize: `${getOr('10240', 'file.max', validation)}KB`,
        minFileSize: `${getOr('1', 'file.min', validation)} bytes`,
        url: `${API_HOST}/bookings/${bookingID}/quotes/${quoteID}/claims/files/${searchParams}`,
        auth: isAuthRequired,
        apiFieldName: 'file',
        // @ts-ignore ts-migrate(2569) FIXME: Type 'Set<unknown>' is not an array type or a stri... Remove this comment to see the full error message
        acceptedFileTypes: [...new Set(acceptedFileTypes)],
        allowedFileTypes: allowedFileTypes.join(', '),
        localStorageKey: `${name}_${bookingID}`,
        helpText: intl.formatMessage(commonMessages.fileInputHelpText, {
          fileTypes: allowedFileTypes.join(', '),
        }),
        description: meta?.description,
      };
    }
    default:
      // TODO: Add geolocation field type
      return { type };
  }
};
